import React from "react";
import { Atom, Layers, TrendingUp, Factory, Boxes, AlertTriangle, ShieldCheck, Globe2, Zap, FlaskConical } from "lucide-react";
import { Hero, Section, Sub, Callout, Quote, NumberedItem, Bold } from "../modules/_shared";

export default function MetalsPage() {
  return (
    <div data-testid="view-metals" className="max-w-5xl">
      <Hero
        moduleNum="06"
        eyebrowLabel="Secteur"
        italic="Métaux"
        normal="industriels"
        accent="& précieux"
        icon={Atom}
        description="Cuivre, aluminium, zinc, plomb, nickel, étain, argent, cobalt, magnésium — cours complet : grades, normes LME/COMEX, prix historiques, primes régionales, formes commerciales et procédure deal complète, exactement comme dans le PDF de référence."
      />

      <Section eyebrow="Introduction" title="Le pilier transparent du commerce international" index="01">
        <p>
          Les métaux représentent l&apos;un des piliers du commerce international de matières premières.
          Contrairement au pétrole ou aux produits agricoles, ils ont la particularité d&apos;être recyclables à
          l&apos;infini sans perte de qualité, d&apos;être cotés sur des marchés organisés et transparents, et d&apos;avoir
          des standards de qualité internationaux très précis et universellement reconnus. Pour un
          intermédiaire, cette transparence est à <Bold>double tranchant</Bold> : elle facilite la vérification des
          prix (impossible de tricher sur le cours LME), mais elle rend aussi la marge de négociation plus
          étroite et exige une connaissance technique pointue.
        </p>
        <p>
          Le marché mondial des métaux industriels représente plusieurs <Bold>milliers de milliards de dollars</Bold> de
          transactions annuelles. Les principaux acteurs sont les mines, les fonderies, les raffineries, les
          traders institutionnels (Glencore, Trafigura, Vitol, Gunvor), les industriels utilisateurs finaux
          (constructeurs automobiles, fabricants d&apos;emballages, industries électroniques), et les
          intermédiaires comme vous.
        </p>
        <Callout icon={Globe2} title="Bourses de référence">
          La bourse de référence mondiale est le <Bold>London Metal Exchange (LME)</Bold>, fondé en 1877.
          Les prix LME servent de référence pour la quasi-totalité des contrats de métaux industriels.
          Autre bourse importante : <Bold>COMEX</Bold> (CME Group, New York) — principalement pour or, argent et cuivre.
        </Callout>
      </Section>

      {/* ============= CUIVRE ============= */}
      <Section eyebrow="Partie 01 — Cu" title={'Le cuivre — « Dr. Copper »'} index="02">
        <Sub title="Généralités et importance stratégique">
          <p>
            Le cuivre est souvent surnommé <em>« Dr. Copper »</em> par les économistes car son prix est
            considéré comme un excellent indicateur de la santé de l&apos;économie mondiale. Quand l&apos;économie
            est en croissance, la demande en cuivre augmente (construction, électricité, industrie), et son
            prix monte. Quand l&apos;économie ralentit, le prix du cuivre chute. C&apos;est l&apos;un des métaux les plus
            suivis au monde.
          </p>
          <p>
            Le cuivre est indispensable à la transition énergétique : une voiture électrique nécessite
            <Bold> 4 fois plus de cuivre</Bold> qu&apos;un véhicule thermique (~ 83 kg vs 23 kg). Un parc éolien offshore
            nécessite <Bold>8 tonnes de cuivre par MW installé</Bold>. La demande mondiale devrait doubler d&apos;ici 2035.
          </p>
          <ProducersBar
            label="Producteurs"
            items={[
              { country: "Chili", pct: "28 %" },
              { country: "Pérou", pct: "11 %" },
              { country: "Congo", pct: "10 %" },
              { country: "Chine", pct: "9 %" },
              { country: "USA", pct: "6 %" },
            ]}
          />
          <ProducersBar
            label="Consommateurs"
            items={[
              { country: "Chine", pct: "55 %" },
              { country: "UE", pct: "10 %" },
              { country: "USA", pct: "7 %" },
            ]}
          />
        </Sub>

        <Sub title="Les grades et qualités du cuivre">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <GradeCard
              accent
              name="Grade A LME (Copper Cathode Grade A)"
              purity="99,9935 % Cu min"
              body="Standard de référence mondiale. Cathodes 914 × 914 × 12 mm, ~ 125 kg/cathode. Lots de 25 t (200 cathodes). Normes BS EN 1978:1998 / ASTM B115. Câbles, fils, tubes, connecteurs, circuits imprimés."
            />
            <GradeCard
              name="Grade B (Blister Copper)"
              purity="98,5–99,5 %"
              body="Produit intermédiaire issu de la fusion, avant électro-raffinage. Vendu aux raffineries. Prix inférieur de 1,5 % à 3 % vs Grade A."
            />
            <GradeCard
              name="Grade 1 Recyclé (Birch / Berry)"
              purity="≥ 99 %"
              body="Câbles électriques propres sans isolation. Décote 3 % à 8 % vs Grade A LME."
            />
            <GradeCard
              name="Grade 2 Recyclé (Candy)"
              purity="96–99 %"
              body="Câbles avec traces d&apos;isolation, tuyaux usagés. Décote 10 % à 20 % vs Grade A."
            />
            <GradeCard
              name="OFHC (Oxygen-Free High Conductivity)"
              purity="≥ 99,99 %, O₂ < 5 ppm"
              body="Supraconducteurs, équipements médicaux, audio HiFi. Prime de 5 % à 15 % selon qualité et demande."
            />
            <GradeCard
              name="ETP (Electrolytic Tough Pitch)"
              purity="≥ 99,9 %, O₂ 200–400 ppm"
              body="Le cuivre le plus courant pour câbles et fils électriques. Équivalent Grade A en pratique."
            />
          </div>
        </Sub>

        <Sub title="Prix et cotation">
          <p>
            Le cuivre est coté au LME en USD par tonne métrique. <Bold>Trois prix</Bold> publiés chaque jour :
            Cash (spot, livraison J+2), <Bold>3 mois</Bold> (référence pour les contrats internationaux),
            Settlement (clôture officielle, valorisation des stocks). Aussi coté au COMEX (NY).
          </p>
          <PriceTable
            rows={[
              { y: "2020", v: "~ 6 200 $/t" },
              { y: "2021", v: "~ 9 300 $/t (post-COVID)" },
              { y: "2022", v: "~ 8 800 $/t" },
              { y: "2023", v: "~ 8 500 $/t" },
              { y: "2024", v: "~ 9 000 – 9 500 $/t" },
            ]}
            record="Record : 10 730 $/t (mars 2022, frôlé en 2024)"
          />
          <p className="text-sm italic text-[#A1A1AA]">
            Vérifiez toujours le cours en temps réel sur lme.com ou investing.com avant toute négociation.
          </p>
        </Sub>

        <Sub title="Formation du prix dans un deal commercial">
          <p>
            <Bold>Prix deal</Bold> = Prix LME 3 mois (au jour de chargement / pricing convenu) + Prime/décote
            + Fret (si CIF) + Assurance (si CIF) + Commission intermédiaire.
          </p>
          <p>La prime/décote dépend de :</p>
          <ul className="list-disc list-inside space-y-1 text-sm">
            <li>La qualité exacte (Grade A pur ou écarts mineurs)</li>
            <li>La forme (cathodes, lingots, fils, barres)</li>
            <li>Le lieu de livraison (Europe, Asie, Afrique — primes différentes)</li>
            <li>Le volume (gros volumes = parfois légère décote)</li>
            <li>La qualité de l&apos;emballage et du certificat d&apos;origine</li>
          </ul>
          <h5 className="font-serif text-lg text-white mt-6">Primes régionales indicatives sur LME 3 mois</h5>
          <RegionalPrimes
            rows={[
              { region: "Europe (Rotterdam, Hambourg)", value: "+ 80 à 180 $/t" },
              { region: "Asie (Shanghai)", value: "+ 70 à 150 $/t" },
              { region: "USA (Midwest, marché isolé)", value: "+ 150 à 300 $/t" },
              { region: "Afrique (variable selon port)", value: "+ 100 à 250 $/t" },
            ]}
          />
        </Sub>

        <Sub title="Processus d&apos;achat du cuivre — 7 étapes">
          <NumberedItem n="01" title="Qualification de la demande">
            Grade exact (LME Grade A, OFHC, scrap…) · volume (tonnes métriques) · fréquence (spot ou contrat
            annuel) · port de destination · incoterm · paiement · certificats requis (SGS, Bureau Veritas,
            origine) · délai de livraison.
          </NumberedItem>
          <NumberedItem n="02" title="Sourcing du vendeur">
            Réseau direct (raffineries, mines au Chili, Congo, Pérou), plateformes (Metal Bulletin,
            Fastmarkets), ou intermédiaires (NCNDA obligatoire). Exiger : warehouse receipt, certificat de
            stock en entrepôt LME, mandat officiel si mandataire, specs techniques complètes.
          </NumberedItem>
          <NumberedItem n="03" title="Documentation préliminaire">
            NCNDA signé par toutes les parties → LOI acheteur (grade, volume, prix LME + prime, incoterm,
            paiement, port) → FCO vendeur en réponse.
          </NumberedItem>
          <NumberedItem n="04" title="Vérification et due diligence">
            Registre commercial, KYC, vérification stocks via inspecteur indépendant. Comparer prix au LME du
            jour + prime régionale standard. Prix &gt; 5 % sous LME pour Grade A = <Bold>signal d&apos;alarme sérieux</Bold>.
          </NumberedItem>
          <NumberedItem n="05" title="Contrat et financement">
            Contrat de vente complet · LC documentaire à vue ou à terme, ou TT · IMFPA signé simultanément
            avec toutes les commissions des intermédiaires.
          </NumberedItem>
          <NumberedItem n="06" title="Inspection et expédition">
            Pre-Shipment Inspection SGS / Bureau Veritas. Documents : certificat d&apos;analyse, certificat de
            poids, certificat d&apos;origine, packing list, commercial invoice, Bill of Lading.
          </NumberedItem>
          <NumberedItem n="07" title="Paiement et commission">
            Présentation des documents à la banque émettrice du LC. Vérification de conformité documentaire.
            Déblocage paiement et versement simultané des commissions selon IMFPA.
          </NumberedItem>
        </Sub>

        <Sub title="Incoterms recommandés pour le cuivre">
          <ul className="space-y-2 text-sm">
            <li><Bold>FOB</Bold> port d&apos;embarquement : le plus utilisé pour le cuivre venant d&apos;Amérique du Sud (Chili, Pérou). Vendeur jusqu&apos;au bord du navire ; acheteur gère fret et assurance.</li>
            <li><Bold>CIF</Bold> port de destination : très utilisé Europe / Asie / Afrique. Vendeur inclut fret et assurance dans son prix.</li>
            <li><Bold>CPT</Bold> point de destination convenu : livraisons terrestres intra-européennes ou entrepôts spécifiques.</li>
            <li><Bold>DDP</Bold> lieu de destination finale : vendeur prend tout en charge (y compris droits de douane). Plus simple pour l&apos;acheteur. Rare pour les gros volumes.</li>
          </ul>
        </Sub>
      </Section>

      {/* ============= ALUMINIUM ============= */}
      <Section eyebrow="Partie 02 — Al" title="L'aluminium — Le métal de la transition" index="03">
        <Sub title="Généralités et importance stratégique">
          <p>
            L&apos;aluminium est le métal non ferreux le plus produit au monde après le cuivre. À la fois léger
            (densité 2,7 g/cm³ contre 8,9 pour le cuivre), résistant à la corrosion, excellent conducteur
            thermique et électrique, et entièrement recyclable. Omniprésent dans aérospatiale, automobile,
            emballage alimentaire, construction et électronique.
          </p>
          <p>
            La production mondiale dépasse <Bold>65 Mt/an</Bold>. La <Bold>Chine produit ~ 57 %</Bold> de
            l&apos;aluminium primaire mondial — facteur géopolitique et commercial majeur.
          </p>
          <p>
            Contrairement à d&apos;autres métaux, l&apos;aluminium ne se trouve pas à l&apos;état natif. Extrait de la
            <Bold> bauxite</Bold> via deux étapes : procédé Bayer (alumine) puis électrolyse Hall-Héroult
            (aluminium primaire). Très énergivore : <Bold>13 à 16 MWh/t</Bold> → fonderies près des sources
            d&apos;énergie bon marché (hydroélectricité Norvège, Islande, Canada, Russie).
          </p>
        </Sub>

        <Sub title="Les grades et qualités de l'aluminium">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <GradeCard
              accent
              name="A8 (LME Standard)"
              purity="99,80 %"
              body="Grade LME de base. Fe max 0,15 %, Si max 0,10 %, Cu max 0,01 %. Minimum acceptable pour la majorité des applications industrielles. Lingots T-bars, sows, billets, slabs."
            />
            <GradeCard
              name="A7"
              purity="99,70 %"
              body="Confondu avec A8. Très courant sur marchés africains, moyen-orientaux, asiatiques. Produit en Russie (Rusal), UAE (EGA), Bahreïn (Alba), Inde."
            />
            <GradeCard
              name="A85"
              purity="99,85 %"
              body="Grade intermédiaire entre A8 et A95. Prime de 15 à 40 $/t sur LME."
            />
            <GradeCard
              name="A95"
              purity="99,95 %"
              body="Haute pureté. Conductivité électrique optimale, résistance corrosion. Prime 50 à 150 $/t."
            />
            <GradeCard
              name="A99"
              purity="99,99 %"
              body="Ultra-haute pureté. Électronique de précision, condensateurs électrolytiques, alliages spéciaux. Prime 200 à 800 $/t."
            />
            <GradeCard
              name="Super Pur (Five Nines)"
              purity="99,999 %"
              body="Semi-conducteurs, optique, recherche scientifique. Prix × 3 à × 10 vs LME standard."
            />
          </div>
          <Callout icon={AlertTriangle} title="Attention terminologie « A7 »">
            Dans la pratique commerciale (Afrique, Moyen-Orient), <Bold>« A7 »</Bold> désigne souvent une qualité
            premium d&apos;aluminium primaire sans référencer strictement la norme soviétique d&apos;origine
            <Bold> GOST 11069-2001</Bold> (A99 / A95 / A85 / A8 / A7 / A6 / A5).
            <Bold> Toujours demander les spécifications techniques exactes</Bold> (Fe, Si, Cu max).
          </Callout>
          <Sub title="Aluminium recyclé (secondaire)">
            <p>
              Représente ~ <Bold>35 %</Bold> de la production mondiale totale. Produit en fondant chutes,
              déchets et produits en fin de vie. Nécessite <Bold>95 % moins d&apos;énergie</Bold> que la production
              primaire. Vendu sous forme d&apos;alliages spécifiques (séries 1000 à 7000).
            </p>
          </Sub>
          <h5 className="font-serif text-lg text-white mt-6">Séries d&apos;alliages techniques</h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
            <AlloyCard s="1000" ex="1050, 1100" body="Aluminium pur > 99 %. Conductivité électrique et thermique. Emballages, condensateurs." />
            <AlloyCard s="2000" ex="2024" body="Al-Cu. Très haute résistance mécanique. Aérospatiale (fuselages, pièces structurelles). Difficile à souder." />
            <AlloyCard s="3000" ex="3003, 3004" body="Al-Mn. Bonne formabilité, résistance corrosion. Canettes, toitures, échangeurs thermiques." />
            <AlloyCard s="5000" ex="5052, 5083" body="Al-Mg. Excellente résistance corrosion marine. Construction navale, structures marines, blindés." />
            <AlloyCard s="6000" ex="6061, 6063" body="Al-Mg-Si. Excellent pour l&apos;extrusion. Profilés architecturaux, cadres fenêtres, structures." />
            <AlloyCard s="7000" ex="7075" body="Al-Zn. La plus haute résistance mécanique. Aérospatiale, sport haute perf, armement." />
          </div>
        </Sub>

        <Sub title="Prix et cotation">
          <p>
            Coté au LME en USD/t. Mêmes structures que le cuivre : Cash, 3 mois, 15 mois, futures jusqu&apos;à 27
            mois.
          </p>
          <PriceTable
            rows={[
              { y: "2020", v: "~ 1 700 $/t" },
              { y: "2021", v: "~ 2 470 $/t" },
              { y: "2022", v: "~ 2 700 $/t · pic 3 800 $ (mars 22, sanctions Rusal)" },
              { y: "2023", v: "~ 2 250 $/t" },
              { y: "2024", v: "~ 2 300 – 2 600 $/t" },
            ]}
            record="Record : 3 849 $/t · mars 2022"
          />
        </Sub>

        <Sub title="Formation du prix dans un deal">
          <p>
            <Bold>Prix commercial</Bold> = LME 3 mois au jour de pricing + Prime régionale + <Bold>Surcharge énergie</Bold> (si applicable)
            + Surcharge alliage (si applicable) + Fret + Assurance + Commission.
          </p>
          <p className="text-sm">
            La <Bold>surcharge énergie</Bold> est un élément particulier à l&apos;aluminium, lié aux coûts d&apos;électricité
            élevés de la production. En période de hausse (2021-2022 en Europe), certains producteurs
            ajoutent une surcharge de <Bold>50 à 300 $/t</Bold>.
          </p>
          <h5 className="font-serif text-lg text-white mt-6">Primes régionales indicatives — A8 LME</h5>
          <RegionalPrimes
            rows={[
              { region: "Europe duty paid (Rotterdam)", value: "+ 150 à 350 $/t" },
              { region: "Europe duty unpaid", value: "+ 50 à 150 $/t" },
              { region: "USA Midwest (marché protégé)", value: "+ 200 à 450 $/t" },
              { region: "Asie (Tokyo, Shanghai)", value: "+ 100 à 200 $/t" },
              { region: "Moyen-Orient (Dubai)", value: "+ 50 à 120 $/t" },
              { region: "Afrique", value: "+ 100 à 250 $/t" },
            ]}
          />
          <p className="text-xs italic text-[#A1A1AA]">
            « Duty paid » inclut les droits de douane européens (6 % sur l&apos;aluminium primaire hors quotas pour les pays tiers).
          </p>
        </Sub>

        <Sub title="Formes commerciales de l'aluminium">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <FormCard name="T-Bar (Type T)" sub="22,5 kg/lingot · faisceaux 1 t (~ 44 lingots)" body="Forme la plus courante en commerce international. Empilable et facilement manutentionnable." />
            <FormCard name="Sows (gueuses)" sub="500 kg à 1 t" body="Moins courants. Refondus directement par les grandes fonderies. Moins pratiques à manipuler." />
            <FormCard name="Extrusion Billets" sub="cylindres ⌀ 203 mm × 6 m" body="Alliage 6063 / 6061. Destinés aux extrudeurs (profilés)." />
            <FormCard name="Rolling Slabs" sub="plaques rectangulaires" body="Pour laminoirs (feuilles, plaques). Spécifications d&apos;alliage précises." />
            <FormCard name="Wire Rod" sub="bobines ⌀ 9,5 – 15 mm" body="Tréfilerie pour câbles et conducteurs électriques." />
          </div>
        </Sub>

        <Sub title="Processus d'achat — spécificités aluminium">
          <ul className="list-disc list-inside space-y-2 text-sm">
            <li><Bold>Qualification</Bold> : forme (T-bar, sow, billet, slab), grade (A8 LME, A7, alliage spécifique), volume (t métriques), fréquence, origine souhaitée.</li>
            <li><Bold>Origine et traçabilité</Bold> : depuis les sanctions contre la Russie en 2022, l&apos;origine est extrêmement sensible. Rusal = un des plus gros producteurs mondiaux. Beaucoup d&apos;acheteurs européens et américains <Bold>n&apos;achètent plus d&apos;aluminium russe</Bold>. Vérifiez toujours les contraintes de votre acheteur.</li>
            <li><Bold>Inspection</Bold> : SGS / Bureau Veritas — certificat d&apos;analyse chimique (spectrométrie), certificat de poids (balance certifiée), certificat d&apos;origine.</li>
          </ul>
        </Sub>
      </Section>

      {/* ============= ZINC ============= */}
      <Section eyebrow="Partie 03 — Zn" title="Le zinc — Le bouclier anti-corrosion" index="04">
        <Sub title="Généralités">
          <p>
            4ᵉ métal le plus produit au monde. <Bold>~ 50 % de la consommation = galvanisation</Bold>
            (acier protégé contre la corrosion). Sans le zinc, ponts, toitures, automobiles et structures
            métalliques rouilleraient beaucoup plus rapidement. Autres usages : laiton (Cu-Zn), die casting
            (moulage sous pression auto et électronique), peintures anti-corrosion, batteries zinc-carbone.
          </p>
          <ProducersBar
            label="Producteurs"
            items={[
              { country: "Chine", pct: "35 %" },
              { country: "Pérou", pct: "11 %" },
              { country: "Australie", pct: "10 %" },
              { country: "Inde", pct: "7 %" },
              { country: "USA", pct: "6 %" },
            ]}
          />
        </Sub>
        <Sub title="Les grades du zinc">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <GradeCard
              accent
              name="Zinc Special High Grade (SHG) — LME"
              purity="99,995 % Zn"
              body="Standard de référence mondial, seul grade coté au LME. Pb max 0,003 %, Cd max 0,003 %, Fe max 0,002 %, autres max 0,001 %. Lingots 25 kg, faisceaux 1 t (40 lingots)."
            />
            <GradeCard
              name="High Grade (HG)"
              purity="99,95 % Zn"
              body="Galvanisation et moulage sous pression. Décote 0,5 % à 2 % vs SHG."
            />
            <GradeCard
              name="Good Ordinary Brand (GOB)"
              purity="98 – 99,5 % Zn"
              body="Grade inférieur, moins courant. Galvanisation moins exigeante."
            />
            <GradeCard
              name="Zinc recyclé (secondaire)"
              purity="variable"
              body="Issu de chutes de galvanisation, cendres et dross. Décote significative vs LME."
            />
          </div>
        </Sub>
        <Sub title="Prix">
          <PriceTable
            rows={[
              { y: "2020", v: "~ 2 270 $/t" },
              { y: "2021", v: "~ 3 000 $/t" },
              { y: "2022", v: "~ 3 480 $/t · pic 4 500 $ (avril 22)" },
              { y: "2023", v: "~ 2 650 $/t" },
              { y: "2024", v: "~ 2 500 – 3 000 $/t" },
            ]}
            record="Record : 4 580 $/t (2006), approché en 2022"
          />
          <h5 className="font-serif text-lg text-white mt-6">Primes régionales — Zinc SHG</h5>
          <RegionalPrimes
            rows={[
              { region: "Europe (Rotterdam, duty paid)", value: "+ 100 à 250 $/t" },
              { region: "USA Midwest", value: "+ 150 à 300 $/t" },
              { region: "Asie", value: "+ 100 à 200 $/t" },
            ]}
          />
        </Sub>
      </Section>

      {/* ============= PLOMB ============= */}
      <Section eyebrow="Partie 04 — Pb" title="Le plomb — 75 % batteries plomb-acide" index="05">
        <Sub title="Généralités">
          <p>
            L&apos;un des métaux les plus anciennement utilisés par l&apos;humanité. Aujourd&apos;hui, ~ <Bold>75 %</Bold> de
            la consommation mondiale est liée aux batteries plomb-acide (démarrage véhicules thermiques, secours
            industriel). Malgré la montée des VE, la demande reste forte.
          </p>
          <Callout icon={AlertTriangle} title="Métal toxique — réglementation stricte">
            Manipulation, transport et traitement strictement réglementés : classification IMDG (transport
            maritime), étiquetage obligatoire, FDS/MSDS obligatoires, licences d&apos;importation dans certains pays.
            Vérifiez que vos contreparties ont les autorisations nécessaires.
          </Callout>
          <ProducersBar
            label="Producteurs"
            items={[
              { country: "Chine", pct: "45 %" },
              { country: "Australie", pct: "10 %" },
              { country: "USA", pct: "7 %" },
              { country: "Pérou", pct: "6 %" },
            ]}
          />
        </Sub>
        <Sub title="Les grades du plomb">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <GradeCard
              accent
              name="Soft Lead « Corroding » (LME)"
              purity="99,97 % Pb min"
              body="Standard LME. Cu max 0,0015 %, Bi max 0,015 %, Ag max 0,001 %. Lingots 25-50 kg. Accumulateurs, blindage rayonnement X, revêtements anti-corrosion, tuyaux, balles."
            />
            <GradeCard
              name="Hard Lead (Pb-Sb)"
              purity="99,9 – 99,97 %"
              body="Alliage plomb-antimoine (1 % à 6 % Sb). Plus dur. Plaques de batteries, armatures."
            />
            <GradeCard
              name="Recyclé (Secondary)"
              purity="variable"
              body="~ 85 % du plomb occidental = recyclé (batteries usagées). Décote 2 % à 8 % vs LME."
            />
            <GradeCard
              name="Pb-Sn (Solder)"
              purity="alliages"
              body="Soudure électronique. Réduit par directive RoHS UE mais encore très utilisé dans certaines industries."
            />
          </div>
        </Sub>
        <Sub title="Prix">
          <PriceTable
            rows={[
              { y: "2020", v: "~ 1 830 $/t" },
              { y: "2021", v: "~ 2 200 $/t" },
              { y: "2022", v: "~ 2 150 $/t" },
              { y: "2023", v: "~ 2 100 $/t" },
              { y: "2024", v: "~ 2 000 – 2 200 $/t" },
            ]}
            record="Record : ~ 3 890 $/t (2007)"
          />
          <h5 className="font-serif text-lg text-white mt-6">Primes régionales — Plomb</h5>
          <RegionalPrimes
            rows={[
              { region: "Europe (Rotterdam)", value: "+ 20 à 80 $/t" },
              { region: "USA", value: "+ 30 à 100 $/t" },
              { region: "Asie", value: "+ 20 à 60 $/t" },
            ]}
          />
          <p className="text-xs italic text-[#A1A1AA] mt-2">
            Primes plus faibles que sur les autres métaux : marché moins « premium » et plus localisé (le
            recyclage local réduit la dépendance aux imports).
          </p>
        </Sub>
      </Section>

      {/* ============= NICKEL ============= */}
      <Section eyebrow="Partie 05 — Ni" title="Le nickel — Acier inox + batteries VE" index="06">
        <Sub title="Généralités">
          <p>
            Métal stratégique au cœur de deux tendances majeures : <Bold>aciers inoxydables</Bold> (~ 70 % de
            la demande) et <Bold>batteries pour véhicules électriques</Bold> en forte croissance. Un VE avec
            batterie NMC (Nickel-Manganèse-Cobalt) contient <Bold>30 à 60 kg</Bold> de nickel.
          </p>
          <ProducersBar
            label="Producteurs"
            items={[
              { country: "Indonésie", pct: "50 %" },
              { country: "Philippines", pct: "10 %" },
              { country: "Russie", pct: "9 %" },
              { country: "N.-Calédonie", pct: "5 %" },
            ]}
          />
          <Callout icon={AlertTriangle} title="Le « Nickel Short Squeeze » — 8 mars 2022">
            Le marché du nickel a connu l&apos;un des épisodes les plus spectaculaires de l&apos;histoire des
            commodités. Le prix a doublé en quelques heures, passant de 25 000 $ à
            <Bold> plus de 100 000 $/t</Bold>, forçant le LME à <Bold>suspendre les échanges plusieurs jours</Bold>.
            Le trader chinois Xiang Guangda (« Big Shot ») détenait une position short massive et a failli
            provoquer une crise systémique.
          </Callout>
        </Sub>
        <Sub title="Les grades du nickel">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <GradeCard
              accent
              name="Nickel Primaire LME"
              purity="≥ 99,80 %"
              body="Standard LME. Boulettes (pellets/rounds), cathodes (plates ou lollipops), briquettes. Lots LME 6 t."
            />
            <GradeCard
              name="Full Plate Cathodes"
              purity="99,90 – 99,99 %"
              body="Cathodes complètes issues de l&apos;électrolyse. Premium 50 à 200 $/t sur LME."
            />
            <GradeCard
              name="Nickel Matte"
              purity="~ 70 % Ni + 25 % Co + 5 % Fe/S"
              body="Produit intermédiaire issu de la 1ère fusion du minerai. Vendu aux raffineries. Prix ajusté selon teneur Ni."
            />
            <GradeCard
              name="Ferro-Nickel (FeNi)"
              purity="20 – 40 % Ni"
              body="Indonésie, N.-Calédonie, Grèce (Larco). Utilisé directement pour acier inox. Non coté LME, prix négocié sur base LME nickel."
            />
            <GradeCard
              name="NPI (Nickel Pig Iron)"
              purity="1,5 – 12 % Ni"
              body="Très basse pureté. Chine, Indonésie, à partir de latérite. A révolutionné le marché depuis 2010 : beaucoup moins cher, utilisé pour inox de moindre qualité. Non coté LME."
            />
          </div>
        </Sub>
        <Sub title="Prix">
          <PriceTable
            rows={[
              { y: "2020", v: "~ 13 800 $/t" },
              { y: "2021", v: "~ 18 500 $/t" },
              { y: "2022", v: "~ 25 600 $/t (avec spike à 100 000 $)" },
              { y: "2023", v: "~ 21 500 $/t" },
              { y: "2024", v: "~ 16 000 – 18 000 $/t (surplus indonésiens)" },
            ]}
            record="Record : 101 365 $/t intraday · 8 mars 2022"
          />
          <h5 className="font-serif text-lg text-white mt-6">Primes régionales — Nickel LME</h5>
          <RegionalPrimes
            rows={[
              { region: "Europe (Rotterdam)", value: "+ 100 à 400 $/t" },
              { region: "Asie", value: "+ 50 à 200 $/t" },
            ]}
          />
        </Sub>
      </Section>

      {/* ============= ÉTAIN ============= */}
      <Section eyebrow="Partie 06 — Sn" title="L'étain — Le moins produit, le plus volatil" index="07">
        <Sub title="Généralités">
          <p>
            Métal le moins produit parmi les 6 principaux LME. Usage principal : <Bold>~ 45 % soudure
            électronique</Bold>, protection emballages alimentaires (fer-blanc = acier étamé), alliages.
            Approvisionnement très concentré géographiquement → vulnérable aux perturbations.
          </p>
          <ProducersBar
            label="Producteurs"
            items={[
              { country: "Chine", pct: "35 %" },
              { country: "Indonésie", pct: "25 %" },
              { country: "Birmanie", pct: "15 %" },
              { country: "Pérou", pct: "8 %" },
            ]}
          />
        </Sub>
        <Sub title="Les grades de l'étain">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <GradeCard
              accent
              name="Étain Grade A LME"
              purity="≥ 99,85 % Sn"
              body="Standard LME. Pb max 0,05 %, Cu max 0,05 %, Bi max 0,05 %, As max 0,05 %. Lingots 25 kg, faisceaux 1 t."
            />
            <GradeCard
              name="Étain 99,90 % et 99,99 %"
              purity="haute pureté"
              body="Soudure électronique haute technologie, alliages spéciaux, médical. Primes 200 à 1 000 $/t selon pureté."
            />
          </div>
        </Sub>
        <Sub title="Prix">
          <p>
            Le métal LME aux prix <Bold>les plus élevés en valeur absolue par tonne</Bold>, et l&apos;un des plus
            volatils.
          </p>
          <PriceTable
            rows={[
              { y: "2020", v: "~ 17 000 $/t" },
              { y: "2021", v: "~ 32 000 $/t" },
              { y: "2022", v: "~ 31 000 $/t · pic 51 000 $" },
              { y: "2023", v: "~ 25 000 $/t" },
              { y: "2024", v: "~ 28 000 – 35 000 $/t" },
            ]}
            record="Record : 51 000 $/t · mars 2022"
          />
        </Sub>
      </Section>

      {/* ============= ARGENT ============= */}
      <Section eyebrow="Partie 07 — Ag" title="L'argent — Métal précieux ET industriel" index="08">
        <Sub title="Généralités">
          <p>
            Double nature : à la fois métal précieux (thésaurisation) et métal industriel. Environ <Bold>70 à
            80 fois moins cher que l&apos;or</Bold> (ratio variable). Quand le ratio dépasse <Bold>80:1</Bold>,
            beaucoup d&apos;investisseurs considèrent l&apos;argent comme sous-évalué.
          </p>
          <p>
            <Bold>~ 70 % de la production = sous-produit</Bold> de l&apos;extraction de cuivre, plomb, zinc, or.
            Cela signifie que la production d&apos;argent dépend en partie de la demande pour ces autres métaux.
          </p>
          <ProducersBar
            label="Producteurs"
            items={[
              { country: "Mexique", pct: "23 %" },
              { country: "Chine", pct: "14 %" },
              { country: "Pérou", pct: "14 %" },
              { country: "Chili", pct: "9 %" },
              { country: "Russie", pct: "8 %" },
            ]}
          />
          <h5 className="font-serif text-lg text-white mt-6">Répartition de la demande</h5>
          <ul className="list-disc list-inside text-sm space-y-1">
            <li><Bold>55 % industrie</Bold> : panneaux photovoltaïques (~ 20 g d&apos;argent / panneau), électronique (contacts, claviers), médical (antibactérien, équipements), catalyse chimique</li>
            <li><Bold>30 % investissement</Bold> et réserve de valeur : lingots, pièces, ETF</li>
            <li><Bold>15 % orfèvrerie / joaillerie</Bold> : couverts, bijoux, argenterie</li>
          </ul>
        </Sub>
        <Sub title="Grades et formes commerciales">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <GradeCard
              accent
              name="Argent 999 (Fine Silver / .999)"
              purity="99,9 %"
              body="Standard mondial. Livrable LBMA et COMEX. Lingots 1 000 oz troy (~ 31,1 kg) — « Good Delivery Bars »."
            />
            <GradeCard
              name="9999 (Four Nines / .9999)"
              purity="99,99 %"
              body="Ultra-haute pureté. Applications industrielles spécifiques, pièces premium. Prime 5 % à 15 % sur spot."
            />
            <GradeCard
              name="Britannia (.9584)"
              purity="95,84 %"
              body="Alliage traditionnel britannique. Orfèvrerie et pièces commémoratives."
            />
            <GradeCard
              name="Sterling (.925)"
              purity="92,5 % Ag + 7,5 % Cu"
              body="Standard joaillerie / argenterie. Pas utilisé en commerce de commodités."
            />
            <GradeCard
              name="Lingot 100 oz"
              purity="999 typique"
              body="~ 3,11 kg. Format dealers et industriels. Plus facile à manipuler que le 1 000 oz."
            />
            <GradeCard
              name="Granules / Powder"
              purity="999 typique"
              body="Industrie directe (PV, contacts). Vendu avec prime de fabrication sur spot."
            />
            <GradeCard
              name="Doré Bar"
              purity="60 – 90 % Ag (variable)"
              body="Lingots bruts sortie de mine, contenant Ag + Au. Vendus aux raffineries selon titre Ag et Au."
            />
          </div>
          <Callout icon={ShieldCheck} title="Critères LBMA Good Delivery (argent)">
            <ul className="list-disc list-inside space-y-1">
              <li>Finesse minimale 999/1000</li>
              <li>Poids 750 à 1 100 oz troy par lingot (~ 23 à 34 kg)</li>
              <li>Fabricant agréé liste LBMA Good Delivery</li>
              <li>Marquages obligatoires : finesse, poids, n° série, symbole fabricant</li>
            </ul>
          </Callout>
        </Sub>
        <Sub title="Prix et conversion">
          <p>Le prix est fixé 2× par jour par le LBMA (Silver Price AM et PM), en USD/oz troy. Coté en temps réel sur COMEX.</p>
          <PriceTable
            rows={[
              { y: "2020", v: "~ 20,55 $/oz" },
              { y: "2021", v: "~ 25,14 $/oz" },
              { y: "2022", v: "~ 21,73 $/oz" },
              { y: "2023", v: "~ 23,35 $/oz" },
              { y: "2024", v: "~ 27 – 32 $/oz (forte hausse)" },
            ]}
            record="Record : 49,51 $/oz (avril 2011)"
          />
          <Callout icon={TrendingUp} title="Conversions utiles">
            <ul className="list-disc list-inside space-y-1">
              <li>1 oz troy = 31,1035 g</li>
              <li>1 kg = 32,1507 oz troy</li>
              <li>1 t = 32 150,7 oz troy</li>
              <li><Bold>1 t à 30 $/oz = 964 521 $</Bold></li>
            </ul>
          </Callout>
        </Sub>
        <Sub title="Facteurs influençant le prix">
          <ul className="list-disc list-inside text-sm space-y-1">
            <li><Bold>Ratio or/argent</Bold> : élevé (&gt; 80) = argent sous-évalué ; faible (&lt; 50) = surévalué</li>
            <li><Bold>Demande industrielle solaire</Bold> : croissance des installations PV = driver majeur</li>
            <li><Bold>Position des fonds spéculatifs (COT Report)</Bold> : rapport CFTC publié chaque vendredi. Excès de positions short = pression baissière potentielle</li>
            <li><Bold>Dollar US</Bold> : argent monte quand le dollar baisse, et inversement</li>
          </ul>
        </Sub>
        <Sub title="Sourcing et vérification">
          <p>
            Raffineries agréées LBMA (Asahi, PAMP Suisse, Argor-Heraeus, Metalor, Rand Refinery, Royal
            Canadian Mint, MKS PAMP), mines avec raffinage intégré (Fresnillo Mexique, Hochschild Pérou),
            traders et négociants primaires (Glencore, Trafigura, MKS PAMP, INTL FCStone).
          </p>
          <h5 className="font-serif text-lg text-white mt-4">Documents spécifiques</h5>
          <ul className="list-disc list-inside text-sm space-y-1">
            <li>Certificat d&apos;analyse LBMA</li>
            <li>Assay Report (rapport d&apos;essai)</li>
            <li>Warehouse Receipt (récépissé d&apos;entrepôt)</li>
            <li>Chain of Custody (traçabilité depuis mine ou raffinerie)</li>
          </ul>
          <h5 className="font-serif text-lg text-white mt-4">Vérification d&apos;authenticité</h5>
          <p className="text-sm">
            Les lingots LBMA portent un n° de série unique. En cas de doute, contacter directement la
            raffinerie. Tests : <Bold>XRF</Bold> (fluorescence X, non-destructif), <Bold>ultrasons</Bold>
            (détecte tungstène plaqué — densité tungstène très proche de l&apos;or mais moins de l&apos;argent),
            pesée précise + comparaison aux dimensions standard.
          </p>
          <h5 className="font-serif text-lg text-white mt-4">Incoterms pour l&apos;argent</h5>
          <ul className="list-disc list-inside text-sm space-y-1">
            <li><Bold>CIP</Bold> : très utilisé pour petites quantités transportées par voie aérienne sécurisée</li>
            <li><Bold>DDP</Bold> : livraison clé en main avec douane payée. Clients finaux ou coffres-forts</li>
            <li><Bold>EXW</Bold> : transferts entre coffres sécurisés (ex. Brink&apos;s London → Malca-Amit Genève)</li>
          </ul>
        </Sub>
      </Section>

      {/* ============= COBALT ============= */}
      <Section eyebrow="Partie 08 — Co" title="Le cobalt — Le métal stratégique éthique" index="09">
        <Sub title="Généralités">
          <p>
            L&apos;un des métaux les plus stratégiques du XXIème siècle, irremplaçable dans les batteries
            lithium-ion (smartphones, laptops, véhicules électriques). Coté au LME depuis 2010.
          </p>
          <Callout icon={AlertTriangle} title="Concentration extrême + enjeu éthique">
            <Bold>~ 70 % de la production mondiale = République Démocratique du Congo (RDC)</Bold>.
            Crée une dépendance géopolitique et des problèmes éthiques sérieux liés au travail des enfants
            dans les mines artisanales (ASM — Artisanal and Small-scale Mining). Tesla, Apple, Samsung,
            LG Chem investissent massivement pour sécuriser des sources certifiées éthiques.
          </Callout>
        </Sub>
        <Sub title="Les grades du cobalt">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <GradeCard
              accent
              name="Standard Grade LME"
              purity="≥ 99,80 %"
              body="Standard de référence. Lingots 15-25 kg. Lots LME 1 t."
            />
            <GradeCard
              name="High Grade"
              purity="99,90 % et 99,95 %"
              body="Batteries et superalliages aérospatiaux. Prix supérieur LME."
            />
            <GradeCard
              name="Cobalt en poudre"
              purity="variable"
              body="Cathodes batteries. Plus forte croissance de demande du segment."
            />
            <GradeCard
              name="Hydroxyde Co(OH)₂"
              purity="intermédiaire"
              body="Issu RDC, exporté vers la Chine pour raffinage. Prix exprimé en % du LME selon teneur Co."
            />
          </div>
        </Sub>
        <Sub title="Prix">
          <p>L&apos;un des marchés les plus volatils parmi les métaux industriels.</p>
          <PriceTable
            rows={[
              { y: "2020", v: "~ 33 000 $/t" },
              { y: "2021", v: "~ 51 000 $/t" },
              { y: "2022", v: "~ 72 000 $/t · pic 81 000 $" },
              { y: "2023", v: "~ 33 000 $/t (chute, surplus indonésiens)" },
              { y: "2024", v: "~ 25 000 – 35 000 $/t" },
            ]}
            record="Record : ~ 95 000 $/t (2018, premier boom VE)"
          />
        </Sub>
      </Section>

      {/* ============= MAGNÉSIUM ============= */}
      <Section eyebrow="Partie 09 — Mg" title="Le magnésium — Le métal le plus léger" index="10">
        <Sub title="Généralités">
          <p>
            Densité <Bold>1,74 g/cm³</Bold> — 35 % plus léger que l&apos;aluminium. Massivement utilisé dans
            l&apos;allègement des véhicules (auto, avions) et la fabrication d&apos;alliages. Aussi très important
            en médecine (implants biodégradables) et engrais (magnésie agricole).
          </p>
          <Callout icon={AlertTriangle} title="85 % de la production mondiale = Chine">
            Crée une dépendance extrême. La <Bold>crise énergétique chinoise de l&apos;automne 2021</Bold> a
            temporairement réduit la production de 50 %, faisant flamber les prix et menaçant les chaînes
            d&apos;approvisionnement automobiles européennes. Le magnésium <Bold>n&apos;est pas coté au LME</Bold> —
            son prix est déterminé par négociation directe (Fastmarkets, Metal Bulletin).
          </Callout>
        </Sub>
        <Sub title="Les grades du magnésium">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <GradeCard accent name="Mg 99,90 % (Grade 1)" purity="Al max 0,05 %, Ca max 0,02 %" body="Grade commercial standard pour la plupart des alliages auto et aéronautiques." />
            <GradeCard name="Mg 99,80 % (Grade 2)" purity="standard fonderie" body="Grade courant pour alliages de fonderie moins exigeants." />
            <GradeCard name="Mg 99,95 % et 99,99 %" purity="haute pureté" body="Applications spéciales — nucléaire, médical, militaire." />
          </div>
        </Sub>
      </Section>

      {/* ============= INCOTERMS RÉCAP ============= */}
      <Section eyebrow="Synthèse" title="Incoterms recommandés par métal" index="11">
        <ul className="list-disc list-inside text-sm space-y-2">
          <li><Bold>FOB port d&apos;embarquement</Bold> : standard pour cuivre, aluminium, zinc, plomb, nickel — vendeur jusqu&apos;au navire</li>
          <li><Bold>CIF port destination</Bold> : très utilisé pour livraisons Europe, Asie, Afrique — vendeur inclut fret + assurance</li>
          <li><Bold>CFR</Bold> : acheteur gère sa propre assurance maritime</li>
          <li><Bold>CIP / DDP</Bold> : argent et métaux précieux — transport sécurisé clé en main</li>
          <li><Bold>EXW</Bold> : transferts entre coffres ou entrepôts agréés (LBMA, LME)</li>
        </ul>
      </Section>

      {/* ============= PROCÉDURE GÉNÉRIQUE ============= */}
      <Section eyebrow="Process" title="Acheter / vendre — la séquence en 7 étapes" index="12">
        <NumberedItem n="01" title="Qualification de la demande">
          Grade exact (LME, OFHC, scrap, alliage…) · volume (tonnes métriques) · fréquence (spot ou contrat
          annuel) · port destination · incoterm · paiement · certificats requis (SGS, Bureau Veritas,
          origine) · délai.
        </NumberedItem>
        <NumberedItem n="02" title="Sourcing du vendeur">
          Réseau direct (raffineries, mines), plateformes (Metal Bulletin, Fastmarkets), intermédiaires (NCNDA
          obligatoire). Exiger : warehouse receipt LME, mandat officiel si mandataire, specs techniques complètes.
        </NumberedItem>
        <NumberedItem n="03" title="Documentation préliminaire">
          NCNDA → LOI acheteur → FCO vendeur en réponse.
        </NumberedItem>
        <NumberedItem n="04" title="Vérification & due diligence">
          Registre commercial, KYC, vérification stocks via inspecteur indépendant. Comparer pricing au LME du
          jour + prime régionale standard. Prix &gt; 5 % sous LME pour Grade A = signal d&apos;alarme.
        </NumberedItem>
        <NumberedItem n="05" title="Contrat & financement">
          Contrat de vente complet · LC à vue ou à terme, ou TT · IMFPA signé simultanément avec toutes les
          commissions.
        </NumberedItem>
        <NumberedItem n="06" title="Inspection & expédition">
          Pre-Shipment Inspection SGS / Bureau Veritas. Documents : certificat d&apos;analyse, certificat de poids,
          certificat d&apos;origine, packing list, commercial invoice, Bill of Lading.
        </NumberedItem>
        <NumberedItem n="07" title="Paiement & commission">
          Présentation des documents à la banque émettrice du LC. Vérification de conformité. Déblocage
          paiement et versement simultané des commissions selon IMFPA.
        </NumberedItem>
      </Section>

      <Quote text="Sur les métaux, la marge se gagne par la connaissance technique : un intermédiaire qui maîtrise les grades, les primes régionales et les inspections vaut son poids en or." />
    </div>
  );
}

// ---------- Local subcomponents ----------
const GradeCard = ({ name, purity, body, accent }) => (
  <div className={`border ${accent ? "border-[#D4AF37]/40" : "border-white/10"} bg-[#0c0c0c] p-5 hover:border-[#D4AF37]/60 transition-colors`}>
    <div className="flex items-center gap-3">
      <Layers className="h-4 w-4 text-[#D4AF37]" />
      <div className="font-serif text-base text-white flex-1">{name}</div>
      <div className="font-mono text-xs text-[#D4AF37]">{purity}</div>
    </div>
    <p className="mt-3 text-xs text-[#A1A1AA] leading-relaxed">{body}</p>
  </div>
);

const ProducersBar = ({ label, items }) => (
  <div className="mt-6">
    <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">{label}</div>
    <div className="flex flex-wrap gap-2">
      {items.map((i) => (
        <div key={i.country} className="flex items-center gap-2 px-3 py-2 border border-white/10 bg-[#0c0c0c]">
          <span className="text-sm text-white">{i.country}</span>
          <span className="font-mono text-xs text-[#D4AF37]">{i.pct}</span>
        </div>
      ))}
    </div>
  </div>
);

const PriceTable = ({ rows, record }) => (
  <div className="mt-4 border border-white/10 overflow-hidden">
    <table className="w-full text-sm">
      <tbody className="divide-y divide-white/5">
        {rows.map((r) => (
          <tr key={r.y}>
            <td className="p-3 font-mono text-[#D4AF37] w-20">{r.y}</td>
            <td className="p-3 text-[#C4C4C7]">{r.v}</td>
          </tr>
        ))}
        {record && (
          <tr className="bg-[#141414]">
            <td className="p-3 font-mono text-[#FF3B30] w-20">{"\u2605"}</td>
            <td className="p-3 text-[#F5F5DC] italic">{record}</td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

const RegionalPrimes = ({ rows }) => (
  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
    {rows.map((r) => (
      <div key={r.region} className="flex items-center justify-between border border-white/10 bg-[#0c0c0c] p-4">
        <span className="text-sm text-[#C4C4C7]">{r.region}</span>
        <span className="font-mono text-sm text-[#D4AF37]">{r.value}</span>
      </div>
    ))}
  </div>
);

const FormCard = ({ name, sub, body }) => (
  <div className="border border-white/10 bg-[#0c0c0c] p-5">
    <div className="flex items-center gap-3">
      <Boxes className="h-4 w-4 text-[#D4AF37]" />
      <div>
        <div className="font-serif text-base text-white">{name}</div>
        <div className="font-mono text-[10px] text-[#A1A1AA]">{sub}</div>
      </div>
    </div>
    <p className="mt-3 text-xs text-[#A1A1AA] leading-relaxed">{body}</p>
  </div>
);

const AlloyCard = ({ s, ex, body }) => (
  <div className="border border-white/10 bg-[#0c0c0c] p-5">
    <div className="flex items-center justify-between gap-3">
      <div className="font-mono text-[#D4AF37] text-base">Série {s}</div>
      <div className="font-mono text-[10px] text-[#A1A1AA]">ex. {ex}</div>
    </div>
    <p className="mt-2 text-xs text-[#A1A1AA] leading-relaxed">{body}</p>
  </div>
);
